import { useState, useEffect } from "react";
import {
  DRAWER_WIDTH,
  DrawerContext,
  PAGE_MAX_WIDTH,
  RAIL_WIDTH,
} from "@/providers/DrawerProvider";
import { useSession } from "@/security/RouteGuards";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import IconButton from "@mui/material/IconButton";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import WebshareLogo from "../common/WebshareLogo";
import { Backdrop, Stack, useMediaQuery } from "@mui/material";
import ImgHelper from "../common/ImgHelper";
import { useFederatedUser } from "@/security/federation";
import BaseAppBar from "@/components/navigation/BaseAppBar";
import Banners from "./Banners";
import { StickyBottomBarPlacement } from "../banners/placements/StickyBottomBarPlacement";
import { OverlayPlacement } from "../banners/placements/OverlayPlacement";
import NavMenuMultiPlan from "./NavMenuMultiPlan/NavMenuMultiPlan";
import DrawerExtensionCTA from "./DrawerExtensionCTA";
import SidebarRail from "./SidebarRail/SidebarRail";
import useSidebarCollapsed, { isSidebarShortcut } from "./useSidebarCollapsed";

interface PersistentDrawerLeftProps {
  children: React.ReactNode;
}

const BaseMain = styled("main", {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "drawerWidth",
})(({ theme }) => ({
  // A column at least a screen tall, so the Sticky bottom bar placement, its
  // last child, rests at the bottom of a short page and sticks on a long one.
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  "@supports (height: 100dvh)": { minHeight: "100dvh" },
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
}));

type MainProps = {
  open?: boolean;
  drawerWidth: number;
};

const Main = styled(BaseMain)<MainProps>(({ theme, drawerWidth }) => ({
  variants: [
    {
      props: ({ open }) => Boolean(open),
      style: {
        [theme.breakpoints.up("md")]: {
          width: `calc(100% - ${drawerWidth}px)`,
          marginLeft: `${drawerWidth}px`,
        },
        transition: theme.transitions.create(["margin", "width"], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

const BaseDrawerContainer = styled(Box)(() => ({
  flexDirection: "row",
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

export default function PersistentDrawerLeft({
  children,
}: PersistentDrawerLeftProps) {
  const theme = useTheme();
  const { profile } = useSession();
  const { deleteFederationToken, federationToken } = useFederatedUser();
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = useState(!isMd);
  const [mobileSwitch, setMobileSwitch] = useState(false);
  // From `md` up « collapses the sidebar to its icon rail instead of hiding
  // it (R-2295). Below that there is no rail: the menu button opens the full
  // sidebar over the page, as before, and the saved choice waits for a
  // wider window.
  const { collapsed, setCollapsed, toggleCollapsed } = useSidebarCollapsed();
  const isRail = !isMd && collapsed;
  const drawerWidth = isRail ? RAIL_WIDTH : DRAWER_WIDTH;

  useEffect(() => {
    if (isMd && !mobileSwitch) {
      setMobileSwitch(true);
      open && setOpen(false);
    }
    if (!isMd && mobileSwitch) {
      setMobileSwitch(false);
      !open && setOpen(true);
    }
  }, [isMd, mobileSwitch, open]);

  // ⌘B / Ctrl+B switch between the rail and the full sidebar.
  useEffect(() => {
    if (isMd) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isSidebarShortcut(event)) return;
      event.preventDefault();
      toggleCollapsed();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isMd, toggleCollapsed]);

  const handleDrawerClose = () => {
    if (isMd) {
      setOpen(false);
    } else {
      setCollapsed(true);
    }
  };

  function autoCloseDrawer() {
    if (open && isMd) {
      setOpen(false);
    }
  }

  const DrawerContainer = BaseDrawerContainer;

  return (
    <DrawerContainer>
      <CssBaseline />
      <BaseAppBar
        position="fixed"
        width={drawerWidth}
        open={open}
        setOpen={setOpen}
        expandRail={isRail ? () => setCollapsed(false) : undefined}
        federatedMode={{
          enabled: Boolean(federationToken),
          email: profile?.email,
          release: deleteFederationToken,
        }}
      />
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            overflowX: "hidden",
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        {isRail ? (
          <SidebarRail />
        ) : (
          <>
            <DrawerHeader>
              <Box
                sx={{ margin: 0, pr: "50px" }}
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <WebshareLogo width={150} to="/dashboard" />
              </Box>
              <IconButton
                aria-label="collapse sidebar"
                onClick={handleDrawerClose}
              >
                {theme.direction === "ltr" ? (
                  <ImgHelper
                    alt="doubleChevron"
                    src={
                      theme.palette.mode === "dark"
                        ? "/doubleChevronDarkMode.svg"
                        : "/doubleChevron.svg"
                    }
                  />
                ) : (
                  <>
                    <ChevronRightIcon />
                    <ChevronRightIcon />
                  </>
                )}
              </IconButton>
            </DrawerHeader>

            <NavMenuMultiPlan autoCloseDrawer={autoCloseDrawer} />
            <DrawerExtensionCTA />
          </>
        )}
      </Drawer>
      <Main open={open} drawerWidth={drawerWidth}>
        <DrawerHeader />
        <Banners />
        <Stack alignItems="center">
          <Box width="100%" maxWidth={PAGE_MAX_WIDTH}>
            <DrawerContext.Provider
              value={{
                open,
                drawerWidth,
              }}
            >
              <Box
                sx={[
                  isMd
                    ? {
                        p: 0,
                      }
                    : {
                        // The shared content wrapper owns the vertical padding but
                        // NOT the horizontal padding — each page/section provides its
                        // own 16px horizontal padding. This keeps a single source of
                        // truth and prevents the padding from doubling to 32px when a
                        // page adds its own horizontal padding on top (see R-1700).
                        py: 3,
                        px: 0,
                      },
                ]}
              >
                {children}
              </Box>
            </DrawerContext.Provider>
            <Backdrop
              // Over the page and its sticky banners, under the app bar.
              sx={{
                color: theme.palette.common.white,
                zIndex: theme.zIndex.appBar - 1,
              }}
              open={open && isMd}
              onClick={autoCloseDrawer}
            />
          </Box>
        </Stack>
        <StickyBottomBarPlacement />
        <OverlayPlacement />
      </Main>
    </DrawerContainer>
  );
}
